import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCheck, UserX, Clock, CheckCircle2, MessageCircle, Trash2, Eye, Send, ChevronDown,
} from 'lucide-react';
import { Button } from './ui/button';
import { useUserNames } from '../context/UserNamesContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useFieldDraft } from '../hooks/useFieldDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', icon: MessageCircle, color: 'bg-blue-100 text-blue-800 border-blue-200' },
  snoozed: { label: 'Snoozed', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  resolved: { label: 'Resolved', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

/**
 * ThreadCollaboration — shared inbox collaboration panel.
 *
 * Renders only when the inbox is on the SHARED mailbox (per-user inboxes
 * are private and don't need this layer).
 */
export default function ThreadCollaboration({ threadId, currentUserId, currentUserRole, onChange }) {
  const { resolveName } = useUserNames();
  const [collab, setCollab] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  // Preserve unsent internal note across session timeouts / refreshes.
  const { clearDraft: clearThreadNoteDraft } = useFieldDraft(
    threadId ? `thread-note:${threadId}` : null,
    noteInput,
    setNoteInput
  );
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [viewers, setViewers] = useState([]);
  const heartbeatRef = useRef(null);

  const fetchCollab = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/communications/threads/${threadId}/collab`);
      setCollab(res.data);
    } catch (e) {
      console.error('Failed to load collab:', e);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/communications/assignable-users`);
      setUsers(res.data.users || []);
    } catch (e) {
      console.error('Failed to load assignable users:', e);
    }
  }, []);

  useEffect(() => { fetchCollab(); }, [fetchCollab]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Presence heartbeat — ping on mount and every 20s while thread is open.
  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    const ping = async () => {
      try {
        await axios.post(`${API}/communications/threads/${threadId}/presence`);
        const res = await axios.get(`${API}/communications/threads/${threadId}/presence`);
        if (!cancelled) setViewers(res.data.viewers || []);
      } catch {}
    };
    ping();
    heartbeatRef.current = setInterval(ping, 20000);
    return () => {
      cancelled = true;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [threadId]);

  const updateCollab = async (patch) => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/communications/threads/${threadId}/collab`, patch);
      setCollab(res.data);
      onChange?.(res.data);
    } catch (e) {
      toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  const handleAssign = (userId) => {
    setShowAssignDropdown(false);
    setAssignSearch('');
    if (!userId) {
      updateCollab({ assigned_to_user_id: null });
    } else {
      updateCollab({ assigned_to_user_id: userId });
    }
  };

  const handleStatus = (status) => updateCollab({ status });

  const addNote = async () => {
    const text = noteInput.trim();
    if (!text) return;
    setActing(true);
    try {
      const res = await axios.post(`${API}/communications/threads/${threadId}/notes`, { text });
      setCollab(res.data);
      onChange?.(res.data);
      setNoteInput('');
      clearThreadNoteDraft();
    } catch (e) {
      toast.error(`Failed to post note: ${e.response?.data?.detail || e.message}`);
    } finally {
      setActing(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this internal note?')) return;
    try {
      const res = await axios.delete(`${API}/communications/threads/${threadId}/notes/${noteId}`);
      setCollab(res.data);
      onChange?.(res.data);
    } catch (e) {
      toast.error(`Failed to delete note: ${e.response?.data?.detail || e.message}`);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!assignSearch) return users;
    const q = assignSearch.toLowerCase();
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, assignSearch]);

  const assignToMe = () => updateCollab({ assigned_to_user_id: currentUserId });

  const status = collab?.status || 'open';
  const StatusIcon = STATUS_CONFIG[status]?.icon || MessageCircle;
  const statusColor = STATUS_CONFIG[status]?.color || '';

  return (
    <aside className="border-l bg-muted/10 flex flex-col" data-testid="thread-collaboration">
      <div className="p-3 border-b">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Team</p>
      </div>

      {/* Presence */}
      {viewers.length > 0 && (
        <div className="px-3 py-2 border-b bg-amber-50/60 flex items-start gap-2" data-testid="thread-viewers">
          <Eye size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            {viewers.map(v => v.user_name).join(', ')} {viewers.length === 1 ? 'is' : 'are'} also viewing
          </div>
        </div>
      )}

      {/* Assignment */}
      <div className="p-3 border-b space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Assigned to</p>
        {collab?.assigned_to ? (
          <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-white border" data-testid="assignment-current">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                {(collab.assigned_to.name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{collab.assigned_to.name}</p>
                {collab.assigned_to.email && (
                  <p className="text-[11px] text-muted-foreground truncate">{collab.assigned_to.email}</p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handleAssign(null)}
              disabled={acting}
              title="Unassign"
              data-testid="unassign-btn"
            >
              <UserX size={14} />
            </Button>
          </div>
        ) : (
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-start gap-1.5 text-xs"
              onClick={assignToMe}
              disabled={acting}
              data-testid="assign-to-me-btn"
            >
              <UserCheck size={14} /> Assign to me
            </Button>
          </div>
        )}

        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between text-xs text-muted-foreground"
            onClick={() => setShowAssignDropdown(v => !v)}
            data-testid="assign-other-btn"
          >
            <span>{collab?.assigned_to ? 'Change…' : 'Assign someone else…'}</span>
            <ChevronDown size={12} />
          </Button>
          {showAssignDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowAssignDropdown(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search…"
                    value={assignSearch}
                    onChange={e => setAssignSearch(e.target.value)}
                    className="h-7 text-xs"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">No users found</p>
                  ) : filteredUsers.map(u => (
                    <button
                      key={u.id}
                      onClick={() => handleAssign(u.id)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2"
                      data-testid={`assign-user-${u.id}`}
                    >
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">
                        {(u.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <span className="truncate">{u.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="p-3 border-b space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</p>
        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border ${statusColor}`} data-testid="collab-status-badge">
          <StatusIcon size={12} />
          {STATUS_CONFIG[status]?.label || status}
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Button
              key={key}
              variant={status === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleStatus(key)}
              disabled={acting || status === key}
              className="text-[11px] h-7 px-1"
              data-testid={`status-${key}-btn`}
            >
              {cfg.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="p-3 border-b">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Internal notes ({collab?.notes?.length || 0})
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">Only visible to your team</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (collab?.notes || []).length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No notes yet</p>
          ) : (
            (collab.notes || []).map(note => {
              const canDelete = note.author_id === currentUserId || currentUserRole === 'admin';
              return (
                <div key={note.id} className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-sm" data-testid={`note-${note.id}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold">{resolveName(note.author_name)}</span>
                    <span className="text-[10px] text-muted-foreground">{timeAgo(note.created_at)}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{note.text}</p>
                  {canDelete && (
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="mt-1 text-[10px] text-red-600 hover:underline flex items-center gap-1"
                      data-testid={`delete-note-${note.id}`}
                    >
                      <Trash2 size={10} /> Delete
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="p-3 border-t bg-white">
          <Textarea
            value={noteInput}
            onChange={e => setNoteInput(e.target.value)}
            placeholder="Add an internal note…"
            className="text-sm min-h-[60px] resize-none"
            data-testid="note-input"
          />
          <Button
            size="sm"
            onClick={addNote}
            disabled={acting || !noteInput.trim()}
            className="mt-2 w-full gap-1.5"
            data-testid="add-note-btn"
          >
            <Send size={14} /> Post note
          </Button>
        </div>
      </div>
    </aside>
  );
}
